/**
 * The audio half of voice input, kept free of React and of the browser's audio
 * graph so it can be tested: resampling to 16 kHz, cutting at pauses, and
 * writing WAV.
 *
 * This is the Meet-style path: the page streams short pieces of speech to our
 * own server while the director is still talking, and shows each piece's text
 * as it comes back. Nothing goes to a browser vendor's speech service, which
 * from Iran may not be reachable at all.
 */

export const TARGET_RATE = 16_000

/**
 * Converts whatever rate the microphone runs at (44.1 or 48 kHz, usually) to
 * 16 kHz mono, block by block. Linear interpolation, with the fractional read
 * position carried between blocks so a block boundary leaves no click.
 * Speech below 8 kHz is all a transcriber needs; aliasing above it is inaudible
 * to one.
 */
export class Resampler {
  private readonly step: number
  private pos = 0
  private last = 0

  constructor(inputRate: number) {
    this.step = inputRate / TARGET_RATE
  }

  push(input: Float32Array): Float32Array {
    const out: number[] = []
    // Positions are relative to this block; -1 means «the last sample of the previous one».
    while (this.pos < input.length - 1) {
      const i = Math.floor(this.pos)
      const frac = this.pos - i
      const a = i < 0 ? this.last : (input[i] ?? 0)
      const b = input[i + 1] ?? a
      out.push(a + (b - a) * frac)
      this.pos += this.step
    }
    this.last = input[input.length - 1] ?? this.last
    this.pos -= input.length
    return Float32Array.from(out)
  }
}

export interface SegmenterOptions {
  /** A piece is never cut shorter than this — a lone word transcribes badly. */
  minSeconds: number
  /** Nor longer: the director should see text within this long of speaking. */
  maxSeconds: number
  /** How long a quiet stretch must last to count as a pause. */
  pauseMs: number
}

export const DEFAULT_SEGMENTS: SegmenterOptions = { minSeconds: 2.5, maxSeconds: 9, pauseMs: 400 }

const FRAME = 320 // 20 ms at 16 kHz

/**
 * Cuts the stream into pieces at pauses.
 *
 * Loudness is measured per 20 ms frame against a noise floor learnt from the
 * quietest frames heard so far, so a fan or a busy office raises the bar
 * instead of reading as endless speech. A piece ends at the first pause after
 * `minSeconds`, or at `maxSeconds` regardless — then at the quietest frame of
 * its last second, so a forced cut still avoids the middle of a word.
 *
 * A piece that is silence throughout is dropped: sending it would cost a call
 * and, from some models, a hallucinated sentence.
 */
export class Segmenter {
  private readonly opts: SegmenterOptions
  private samples: number[] = []
  private frame: number[] = []
  private quietFrames = 0
  private voicedFrames = 0
  private frameEnergies: number[] = []
  private noiseFloor = 0.003

  constructor(opts: SegmenterOptions = DEFAULT_SEGMENTS) {
    this.opts = opts
  }

  /** Feeds 16 kHz samples; returns any pieces completed by them. */
  push(input: Float32Array): Float32Array[] {
    const done: Float32Array[] = []
    for (const s of input) {
      this.samples.push(s)
      this.frame.push(s)
      if (this.frame.length === FRAME) {
        const cut = this.onFrame()
        if (cut) done.push(cut)
      }
    }
    return done
  }

  /** Whatever is left when recording stops, if it holds any speech. */
  flush(): Float32Array | null {
    const rest = this.voicedFrames > 0 ? Float32Array.from(this.samples) : null
    this.reset()
    return rest
  }

  private reset(): void {
    this.samples = []
    this.frame = []
    this.quietFrames = 0
    this.voicedFrames = 0
    this.frameEnergies = []
  }

  private onFrame(): Float32Array | null {
    let sum = 0
    for (const s of this.frame) sum += s * s
    const rms = Math.sqrt(sum / FRAME)
    this.frame = []
    this.frameEnergies.push(rms)

    /* The floor follows quiet frames down at once and drifts up very slowly —
       a time constant of about forty seconds — so a long sustained sound is
       not mistaken for the room, while a hum that starts mid-recording is
       learnt within a minute. */
    this.noiseFloor = rms < this.noiseFloor ? rms : this.noiseFloor * 0.9995 + rms * 0.0005
    const voiced = rms > this.threshold()
    if (voiced) {
      this.voicedFrames += 1
      this.quietFrames = 0
    } else {
      this.quietFrames += 1
    }

    const seconds = this.samples.length / TARGET_RATE
    const pauseFrames = this.opts.pauseMs / 20
    if (seconds >= this.opts.minSeconds && this.quietFrames >= pauseFrames) return this.cutAt(this.samples.length)
    if (seconds >= this.opts.maxSeconds) return this.cutAt(this.quietestInLastSecond())
    return null
  }

  private threshold(): number {
    return Math.max(0.01, this.noiseFloor * 3)
  }

  /** The end of the quietest frame in the last second, as a sample index. */
  private quietestInLastSecond(): number {
    const frames = this.frameEnergies.length
    const from = Math.max(0, frames - TARGET_RATE / FRAME)
    let best = frames - 1
    for (let i = from; i < frames; i += 1) {
      if ((this.frameEnergies[i] ?? 1) < (this.frameEnergies[best] ?? 1)) best = i
    }
    return (best + 1) * FRAME
  }

  private cutAt(index: number): Float32Array | null {
    const piece = this.samples.slice(0, index)
    const rest = this.samples.slice(index)
    const restEnergies = this.frameEnergies.slice(Math.ceil(index / FRAME))
    const hadSpeech = this.voicedFrames > 0
    const threshold = this.threshold()
    this.reset()
    // What came after the cut starts the next piece, speech it already holds included.
    this.samples = rest
    this.frameEnergies = restEnergies
    this.voicedFrames = restEnergies.filter((e) => e > threshold).length
    return hadSpeech ? Float32Array.from(piece) : null
  }
}

/** 16 kHz mono 16-bit PCM WAV — the one format every audio model accepts. */
export function encodeWav(samples: Float32Array, rate = TARGET_RATE): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const text = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i += 1) view.setUint8(offset + i, s.charCodeAt(i))
  }
  text(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  text(8, 'WAVE')
  text(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  text(36, 'data')
  view.setUint32(40, samples.length * 2, true)
  for (let i = 0; i < samples.length; i += 1) {
    const v = Math.max(-1, Math.min(1, samples[i] ?? 0))
    view.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true)
  }
  return new Blob([buffer], { type: 'audio/wav' })
}

/** Joins pieces of text as they were spoken, without doubling the spaces between them. */
export function joinPieces(pieces: (string | undefined)[]): string {
  return pieces
    .map((p) => p?.trim() ?? '')
    .filter(Boolean)
    .join(' ')
}
